
import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface QuestionEditorProps {
  question: {
    question_text: string;
    options: string[];
    correct_options: string[];
    points: number;
    order_number: number;
  };
  onChange: (updatedQuestion: any) => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onChange }) => {
  // Handle question text change
  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...question,
      question_text: e.target.value
    });
  };
  
  // Handle points change
  const handlePointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...question,
      points: parseInt(e.target.value) || 1
    });
  };
  
  // Handle option change
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...question.options];
    newOptions[index] = value;
    
    // If this was a correct option, update that as well with the new value
    const correctOptions = question.correct_options.includes(question.options[index])
      ? question.correct_options.map(opt => 
          opt === question.options[index] ? value : opt
        )
      : question.correct_options;
    
    onChange({
      ...question,
      options: newOptions,
      correct_options: correctOptions
    });
  };
  
  // Handle correct option toggle
  const handleCorrectToggle = (option: string, checked: boolean) => {
    const correctOptions = checked
      ? [...question.correct_options, option]
      : question.correct_options.filter(opt => opt !== option);
    
    onChange({
      ...question,
      correct_options: correctOptions
    });
  };
  
  // Add new option
  const addOption = () => {
    onChange({
      ...question,
      options: [...question.options, ""]
    });
  };
  
  // Remove option
  const removeOption = (index: number) => {
    const optionToRemove = question.options[index];
    const newOptions = question.options.filter((_, i) => i !== index);
    const newCorrectOptions = question.correct_options.filter(opt => opt !== optionToRemove);
    
    onChange({
      ...question,
      options: newOptions,
      correct_options: newCorrectOptions
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="flex-grow">
          <label className="text-sm font-medium mb-1 block">Vraag</label>
          <Input 
            value={question.question_text} 
            onChange={handleQuestionTextChange}
            placeholder="Voer de vraag in" 
          />
        </div>
        
        <div className="w-20">
          <label className="text-sm font-medium mb-1 block">Punten</label>
          <Input 
            type="number" 
            min="1"
            value={question.points} 
            onChange={handlePointsChange}
          />
        </div>
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium">Antwoordopties</label>
          <Button type="button" variant="outline" size="sm" onClick={addOption}>
            <Plus className="h-4 w-4 mr-1" /> Optie
          </Button>
        </div>
        
        <div className="space-y-2">
          {question.options.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <Checkbox
                checked={question.correct_options.includes(option)}
                onCheckedChange={(checked) => handleCorrectToggle(option, !!checked)}
                disabled={!option}
              />
              <Input
                className="flex-grow"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Optie ${index + 1}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeOption(index)}
                disabled={question.options.length <= 2}
              >
                <Trash className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-muted-foreground mt-2">
          Vink de juiste antwoorden aan (minstens één).
        </p>
      </div>
    </div>
  );
};

export default QuestionEditor;
